<script>
  import { _, locale } from "./i18n.js";
  import { tick, onDestroy } from "svelte";
  import {
    f7,
    Page,
    List,
    ListItem,
    ListInput,
    Button,
    Block,
    BlockTitle,
    Icon,
    Preloader,
    Link,
    SwipeoutActions,
    SwipeoutButton
  } from "framework7-svelte";
  import Toolbar from "./Toolbar.svelte";
  import { getSecuredFirestore, getCachedFirst } from "./Firestore.js";
  import { getProfile, resetProfile } from "./UserService.js";
  import { getLiffProfile, getLiffLanguage } from "./LiffService.js";
  import { getLanguageFromLocale, checkImage } from "./Utils.js";
  import {
    formatAddress,
    setPrimaryAddress,
    deleteAddress,
    deletableAddress,
    COMPACT
  } from "./AddressService.js";

  export let f7router;

  let name;
  let userDoc;
  let languageSource;

  onDestroy(async () => {
    addressesSnapshotListener && addressesSnapshotListener();
  });

  let lineLanguage;
  $: lineLanguageLabel = lineLanguage ? getLanguageLabel(lineLanguage) : "";
  getLiffLanguage().then(function(language) {
    lineLanguage = getLanguageFromLocale(language);
  });

  let profileLanguage;
  let addresses;
  let addressesSnapshotListener;

  getProfile().then(function(profile) {
    getSecuredFirestore().then(function(firestore) {
      name = profile.name;
      languageSource = profile.languageSource;

      if (profile.language) {
        profileLanguage = profile.language;
      } else {
        // fallback if no language has been set on profile
        getLiffLanguage().then(function(language) {
          profileLanguage = getLanguageFromLocale(language);
        });
      }
      userDoc = firestore.collection("users").doc(profile.userId);

      let query = firestore.collection(
        "users/" + profile.userId + "/addresses"
      );

      addressesSnapshotListener = query.onSnapshot(function(snapshot) {
        let a = [];
        snapshot.forEach(function(doc) {
          a.push({ ...{ id: doc.id }, ...doc.data() });
        });
        addresses = a;
      });
    });
  });

  function updateName(value) {
    name = value;
    userDoc.update({ name: name });
    resetProfile();
  }

  function setDefaults() {
    f7.dialog.confirm($_("settings.setDefaults.confirm"), "Set Default", () => {
      getLiffProfile().then(function(profile) {
        name = profile.displayName;
      });

      languageSource = "line";
      updateLanguageSource(languageSource);
    });
  }

  function updateProfileLanguage(language) {
    profileLanguage = language;
    userDoc.update({ language: language });
    resetProfile();

    if (typeof Storage !== "undefined") {
      localStorage.setItem("language", language);
    }

    if (!$locale.startsWith(language)) {
      locale.set(language);
      resetLanguageApiCache();

      languageSource = null;
      (async function() {
        await tick();
        languageSource = "custom";
      })();
    }
  }

  function updateLanguageSource(source) {
    languageSource = source;
    userDoc.update({ languageSource: source });
    resetProfile();

    let language;
    switch (source) {
      case "line":
        language = lineLanguage;
        break;
      case "custom":
        language = profileLanguage || lineLanguage;
        break;
    }

    if (typeof Storage !== "undefined") {
      localStorage.setItem("language", language);
    }

    if (!$locale.startsWith(language)) {
      locale.set(language);
      resetLanguageApiCache();
    }
  }

  function resetLanguageApiCache() {
    fetch(
      "https://api-dot-tueyhom.df.r.appspot.com/language/clearCache?apiKey=" +
        process.env.GOOGLE_API_KEY +
        "&userId=" +
        userDoc.id
    );
  }

  function getLanguageLabel(language) {
    switch (language) {
      case "en":
        return "English";
      case "th":
        return "ไทย";
    }
  }
</script>

<style>
  .smartSelectLink {
    color: rgb(0, 128, 228);
    font-weight: bold;
  }

  :global(.item-title-row) {
    align-items: center;
  }
</style>

<Page>
  <span style="display: none" class="hidden" />
  <BlockTitle medium>{$_('toolbar.settings')}</BlockTitle>

  <List inlineLabels form noHairlines>
    <ListInput
      type="text"
      placeholder={$_('settings.yourName')}
      value={name}
      onChange={e => updateName(e.target.value)}
      required
      validate>
      <div slot="media">
        {#if userDoc}
          {#await getCachedFirst(userDoc) then user}
            {#if user.get('profilePhoto')}
              {#await checkImage(user.get('profilePhoto')) then status}
                {#if status}
                  <img
                    style="max-width: 40px; border-radius: 50%; margin-right:
                    10px"
                    alt="User Profile"
                    src={user.get('profilePhoto')} />
                {:else}
                  <span
                    style="font-size: 2.5rem; color: rgb(0, 122, 255);
                    margin-right: 10px"
                    class="icon icon-profile" />
                {/if}
              {/await}
            {/if}
          {/await}
        {/if}
      </div>
    </ListInput>
  </List>

  {#if languageSource}
    <List noHairlines>
      {#if languageSource === 'custom'}
        {#if profileLanguage}
          <ListItem
            smartSelect
            smartSelectParams={{ sheetCloseLinkText: $_('base.done'), valueEl: '.hidden', routableModals: false, openIn: 'sheet', on: { closed: function(e) {
                  let language = e.getValue();
                  updateProfileLanguage(language);
                } } }}>
            <span style="font-weight: bold;" slot="title">
              {$_('settings.language')}
            </span>
            <div class="item-after" slot="after-title">
              <span class="smartSelectLink">
                {getLanguageLabel(profileLanguage)}
              </span>
            </div>
            <select
              style="font-weight: bold; color: rgb(0, 122, 255);"
              name="language"
              value={profileLanguage}>
              <option value="en">English</option>
              <option value="th">ไทย</option>
            </select>
          </ListItem>
        {/if}
      {:else}
        <ListItem style="font-weight: bold">
          <span style="font-weight: bold;" slot="title">
            {$_('settings.language')}
          </span>
          <span
            style="font-weight: bold; color: rgb(0, 128, 228);"
            slot="after">
            {getLanguageLabel(getLanguageFromLocale($locale))}
          </span>
        </ListItem>
      {/if}
      <ListItem
        radio
        radioIcon="start"
        title={$_('settings.language.useFromLine')}
        value="line"
        name="languageSource"
        checked={languageSource === 'line'}
        onChange={e => updateLanguageSource(e.target.value)}>
        <span
          slot="after"
          style="margin-left: 6px; font-size: 22px"
          class="icon icon-line" />
      </ListItem>
      <ListItem
        radio
        radioIcon="start"
        title={$_('settings.language.custom')}
        value="custom"
        name="languageSource"
        checked={languageSource === 'custom'}
        onChange={e => updateLanguageSource(e.target.value)}>
        <span
          slot="after-title"
          style="margin-left: 6px; font-size: 22px"
          class="icon icon-custom" />
      </ListItem>
    </List>
  {:else}
    <div
      style="align-items: center; display: flex; justify-content: center;
      flex-direction: column; height: 100%;">

      <Preloader />
    </div>
  {/if}

  <Block>
    <Button style="margin: 10px;" fill round on:click={setDefaults}>
      {$_('settings.setDefaults')}
      <span style="margin-left: 6px" class="icon icon-loop" />
    </Button>
  </Block>

  <BlockTitle medium>{$_('settings.deliveryAddresses')}</BlockTitle>

  {#if !addresses}
    <div
      style="align-items: center; display: flex; justify-content: center;
      flex-direction: column; height: 100%;">

      <Preloader />
    </div>
  {:else if addresses.length > 0}
    <List mediaList>
      {#each addresses as address}
        <ListItem
          swipeout
          radio
          checked={address.primary === true}
          radioIcon="start"
          name="address"
          color="blue"
          on:change={e => setPrimaryAddress(userDoc.id, address.id)}>
          <SwipeoutActions right>
            <SwipeoutButton
              color="red"
              on:click={function(e) {
                let dialog = f7.dialog.preloader('Checking references...');
                deletableAddress(userDoc.id, address.id).then(function(
                  deletable
                ) {
                  dialog.close();
                  if (deletable) {
                    f7.dialog.confirm(
                      $_('address.dialog.deleteAddress.description'),
                      $_('address.dialog.deleteAddress.title'),
                      () => {
                        f7.swipeout.close(f7.swipeout.el, function() {
                          dialog = f7.dialog.preloader(
                            $_('address.dialog.deletingAddress')
                          );
                          deleteAddress(userDoc.id, address.id, address.primary)
                            .then(function() {
                              dialog.close();
                              f7.toast
                                .create({
                                  text: $_(
                                    'address.dialog.deleteAddress.successful'
                                  ),
                                  position: 'top',
                                  closeTimeout: 4000
                                })
                                .open();
                            })
                            .catch(function(e) {
                              console.log(e);
                              dialog.close();
                              f7.dialog.alert(
                                $_('address.dialog.deleteAddress.error'),
                                $_('address.dialog.deleteAddress.title')
                              );
                            });
                        });
                      }
                    );
                  } else {
                    f7.dialog
                      .create({
                        title: $_('address.dialog.deleteAddress.title'),
                        text: $_('address.dialog.deleteAddress.referenced'),
                        buttons: [
                          {
                            text: $_('base.cancel')
                          },
                          {
                            text: $_(
                              'address.dialog.deleteAddress.button.openOrder'
                            ),
                            bold: true,
                            onClick: function() {
                              f7router.navigate('/Order/New');
                            }
                          }
                        ],
                        verticalButtons: false
                      })
                      .open();
                  }
                });
              }}>
              {$_('base.delete')}
            </SwipeoutButton>
          </SwipeoutActions>
          <div
            style={address.primary ? 'font-weight: bold' : ''}
            slot="after-title">
            {formatAddress({
              ...address,
              ...{ mode: COMPACT }
            }) + (address.primary ? ' (PRIMARY)' : '')}
          </div>
          <div slot="after" style="display: flex">
            <Link href="/Address/{userDoc.id}/{address.id}">
              <span
                class="icon icon-external"
                style="margin-right: 20px; font-size: 22px" />
            </Link>
            {#if !address.deliverable}
              <span
                style="font-size: 22px; color: red"
                class="icon icon-undeliverable" />
            {:else if address.location}
              <Link
                external
                target="_new"
                href="https://www.google.com/maps/search/{address.location.latitude},{address.location.longitude}"
                style="font-size: 1.4rem;"
                icon="icon icon-location" />
            {:else}
              <Link
                class="disabled"
                style="font-size: 1.4rem; color: gray"
                icon="icon icon-noLocation" />
            {/if}
          </div>
        </ListItem>
      {/each}
    </List>
  {:else}
    <Block>
      <div>You haven't added any delivery addresses yet.</div>
    </Block>
  {/if}

  {#if addresses && addresses.length < 3}
    <Block>
      <Button
        color="green"
        style="margin: 10px;"
        fill
        round
        href="/Address/{userDoc.id}/New">
        {$_('address.navTitle.add')}
        <span style="margin-left: 6px" class="icon icon-plus" />
      </Button>
    </Block>
  {/if}

  <Toolbar tabLinkActive={'/Settings'} />
</Page>
